import "@pandas/core-ui/fonts.css";
import "../polyfills";
import "../styles/styles.css";

import { DEFAULT_LOCALE, DEFAULT_PARTNER, getLanguageCode, isKnownLocaleTag } from "@pandas/core-ui";
import { canonicalizeRootLocaleUrl, type PartnerRouteContext } from "@pandas/core-ui/route";
import { AblyProvider } from "@pandas/realtime";
import { createRootRouteWithContext, Outlet, redirect } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Suspense } from "react";
import { I18nextProvider } from "react-i18next";
import Loader from "@/components/Loaders/Loader";
import { servicesOrchestratorValues } from "@/config/services";
import logger from "@/lib/logging/logger";
import i18next from "../i18n";

export type RouterContext = PartnerRouteContext;

const showDevtools = false;

const resolveWebappLocale = (legacy: string) => (isKnownLocaleTag(legacy) ? getLanguageCode(legacy) : DEFAULT_LOCALE);

export const Route = createRootRouteWithContext<RouterContext>()({
	notFoundComponent: () => (
		<div className="flex h-screen items-center justify-center">
			<p className="subheading-1">404 — Not Found</p>
		</div>
	),
	beforeLoad: async ({ location, search, context }) => {
		// Root path is handled by index.tsx
		if (location.pathname === "/") return;

		const result = await canonicalizeRootLocaleUrl({
			pathname: location.pathname,
			search,
			isLocale: isKnownLocaleTag,
			resolveLocale: resolveWebappLocale,
			reservedPartnerSegments: context.reservedPartnerRouteSegments,
			resolvePartner: (s) => (typeof s.partnerCode === "string" ? s.partnerCode : null),
		});

		if (result?.kind === "redirect") throw redirect({ href: result.href, replace: true });

		if (result?.kind === "unresolvable") {
			throw redirect({
				to: "/$locale/$partner",
				params: { locale: DEFAULT_LOCALE, partner: DEFAULT_PARTNER },
				replace: true,
			});
		}
	},
	component: RootComponent,
});

function RootComponent() {
	return (
		<AblyProvider
			apiKey={servicesOrchestratorValues.credentials.ablyKey}
			clientId="webapp"
			logger={logger}
		>
			<Suspense fallback={<Loader />}>
				<I18nextProvider i18n={i18next}>
					<Outlet />
					{showDevtools && <TanStackRouterDevtools />}
				</I18nextProvider>
			</Suspense>
		</AblyProvider>
	);
}
